import React, { useState, useCallback, useEffect } from 'react';

import { apiContainer } from '@vlabs/api-bindings';
import { ArrowInCircleRightIcon, DetectionIcon } from '@vlabs/icons';
import { EmptyCard } from '@vlabs/shared/components/empty-card';
import InfoRow from '@vlabs/shared/components/info-row/InfoRow';
import { BaseSample } from '@vlabs/shared/components/sample/BaseSample';
import { SimilarityTag } from '@vlabs/shared/components/similarity-tag/SimilarityTag';
import ListLink from '@vlabs/shared/legacy-components/list-link/ListLink';
import { routes } from '@vlabs/shared/navigation/routes';
import { calcTagType } from '@vlabs/shared/utils';
import { CircleAnimatedButton, Control, useWindowSize } from '@vlabs/uikit';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { generatePath } from 'react-router-dom';

import { selectHandlers } from '@vlabs/pages/handlers/selectors';

import st from './FaceTopMatchCell.module.sass';

const ExternalIdLink = ({ external_id }, size) => {
  return (
    <Control.Link size={size} to={generatePath(routes.externalId.read, { externalId: external_id })}>
      {external_id}
    </Control.Link>
  );
};

const FaceTopMatchCellComponent = ({
  topMatch,
  onClick,
  handlerList = {},
  originalDetections,
}) => {
  const { t } = useTranslation();
  const tagColor = calcTagType(topMatch.similarity);
  const windowSize = useWindowSize();
  const linkAndTagSizeProp = windowSize.width < 1920 ? 'medium' : 'large';

  const [face, setFace] = useState({ ...topMatch, isExist: true });

  const fetchFace = useCallback(async () => {
    try {
      const { data } = await apiContainer.lunaClient.faces.get(topMatch.face_id);

      // Это нужно чтобы получить список в котором числится топМач
      const handler = handlerList?.find(({ handlerId }) => handlerId === topMatch.handler_id);
      const matchPolicy = handler?.matchPolicy;
      const $listId = matchPolicy?.filter(({ label }) => label === topMatch?.label)
        .map(({ listId }) => listId)
        .filter((id) => id !== undefined);

      setFace({ ...data, topMatchList: $listId, ...face });
    } catch ({ error }) {
      // Если лицо удалено - 'Object not found'
      if (error.response.data.error_code === 22002) {
        setFace({ ...face, isExist: false });
        console.error('Face not found:', error);
      } else {
        throw error;
      }
    }
  }, [topMatch?.face_id]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchFace(); }, []);

  return (
    <div className={st.Root}>
      <div className={cn(st.Content, st[`Content_BorderColor_${tagColor}`])}>
        {face?.isExist === true && (
          <div className={st.Image}>
            <div className={st.GoToButton}>
              <CircleAnimatedButton
                icon={<ArrowInCircleRightIcon />}
                onClick={onClick}
                onSurface
                size={windowSize.width < 1920 ? 'small' : 'medium'}
              >
                {t('events:top match card.icon goTo face')}
              </CircleAnimatedButton>
            </div>
            <BaseSample
              data-testid="topMatchSample"
              dataTip={t('events:проверьте настройки сценария')}
              imageUrl={face?.uiAvatarURL}
              type="face"
            />
          </div>
        )}
        {face?.isExist === false && (
          <EmptyCard className={st.EmptyFaceCard} dataTip={t('no data.tooltip no detect')} text={t('faces:лицо удалено')} />
        )}
        <div className={st.InfoPanel}>
          <SimilarityTag icon={<DetectionIcon />} size={linkAndTagSizeProp} title={t('face')} value={topMatch.similarity} />
          <div className={st.Info}>
            {face?.external_id && (
              <InfoRow
                columnDirection
                data-testid="topMatchCell.externalId"
                title={t('external id')}
                value={<ExternalIdLink {...face} size={linkAndTagSizeProp} />}
              />
            )}
            {face?.user_data && (
              <InfoRow
                columnDirection
                data-testid="topMatchCell.userData"
                title={t('information')}
                value={face.user_data}
              />
            )}
            {face?.topMatchList?.length > 0 && (
              <InfoRow
                columnDirection
                data-testid="topMatchCell.lists"
                title={t('list')}
                value={<ListLink lists={face?.topMatchList} size={linkAndTagSizeProp} />}
              />
            )}
          </div>
        </div>
      </div>
      {originalDetections.hasBody && <EmptyCard className={st.EmptyBodyCard} text={t('данные тела отсутствуют')} />}
    </div>
  );
};

FaceTopMatchCellComponent.propTypes = {
  topMatch: PropTypes.object.isRequired,
  onClick: PropTypes.func.isRequired,
  handlerList: PropTypes.object,
  originalDetections: PropTypes.shape({
    hasBody: PropTypes.bool.isRequired,
    hasFace: PropTypes.bool.isRequired,
  }).isRequired,
};

export const FaceTopMatchCell = connect(
  (state) => ({
    handlerList: selectHandlers(state).data,
  }))(FaceTopMatchCellComponent);
